import { makeStyles } from '@material-ui/core/styles';

/**
 * Spacing is on the 2px grid, 16px default and 8px for the dense level rows —
 * the two are never mixed inside one region. Colours come from the FDS theme
 * palette; no raw hex.
 */
export const useStyles = makeStyles(theme => ({
  page: {
    padding: 24,
  },
  // Sits directly under the page title, so it takes the tighter 8px rather than
  // the page's default 16px: title and subtitle are one unit, and spacing them
  // like separate blocks reads as two unrelated sentences.
  subtitle: {
    marginTop: 8,
  },
  pageTabs: {
    marginTop: 24,
  },
  // Runs the full width under the strip, as the design has it — the line is the
  // boundary of the whole tabbed region, not an underline for the tabs.
  pageTabsDivider: {
    marginBottom: 16,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 16,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 16,
  },
  search: {
    maxWidth: 320,
    flexGrow: 1,
  },
  list: {
    border: `1px solid ${theme.palette.common.gray200}`,
    borderRadius: 4,
  },
  rule: {
    padding: 16,
    borderBottom: `1px solid ${theme.palette.common.gray200}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  // A disabled rule stays listed — it is configuration the admin still owns, and
  // hiding it would make "why is this not firing?" unanswerable from this page.
  // Dimming pairs with the "Off" badge: opacity alone would be colour-as-state.
  ruleDisabled: {
    opacity: 0.5,
  },
  ruleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  ruleName: {
    marginRight: 8,
  },
  summary: {
    marginTop: 4,
  },
  // Dense region: the nested level rows sit at 8px, consistently, and never
  // borrow the 16px used by the rule rows around them.
  levels: {
    marginTop: 8,
    paddingLeft: 16,
    borderLeft: `2px solid ${theme.palette.common.gray200}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  // One level row. Dense region, so 8px — the same gap the container uses, never
  // the 16px of the rule rows around it.
  levelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  managedHint: {
    marginTop: 4,
  },
  empty: {
    padding: 48,
    textAlign: 'center',
  },
  emptyBody: {
    marginTop: 8,
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: 16,
    marginBottom: 16,
    border: `1px solid ${theme.palette.common.red400}`,
    background: theme.palette.common.red100,
    borderRadius: 4,
  },
  skeletonRow: {
    padding: 16,
    borderBottom: `1px solid ${theme.palette.common.gray200}`,
  },
  // 640px per the design. Capped at the viewport so the panel does not overflow
  // a narrow window — a drawer wider than the screen puts its own actions off
  // the right edge.
  panel: {
    width: 640,
    maxWidth: '100vw',
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  field: {
    marginTop: 8,
  },
  // A non-ideal state on a level card the admin has to be told about — approvers
  // the save will drop, or a level the rule skips. Coloured *and* worded: the
  // sentence carries the meaning on its own, so the colour is reinforcement
  // rather than the signal.
  inlineWarning: {
    marginTop: 4,
    color: theme.palette.warning.main,
  },
  levelCard: {
    border: `1px solid ${theme.palette.common.gray200}`,
    borderRadius: 4,
    padding: 16,
  },
  levelCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  panelNote: {
    marginTop: 8,
  },
  panelActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 16,
    marginTop: 16,
  },
  editButton: {
    marginLeft: 'auto',
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginLeft: 'auto',
  },
  dialogWarning: {
    marginTop: 8,
    color: theme.palette.common.orange600,
  },
  dialogError: {
    marginTop: 16,
    color: theme.palette.common.red600,
  },
  // Destructive actions never carry primary weight — text, in the error role,
  // sitting after Cancel rather than replacing it as the filled button.
  dangerButton: {
    color: theme.palette.common.red600,
  },
  conditions: {
    marginTop: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  // One condition per row, read left to right as a sentence — "Sign-on bonus /
  // greater than / 10000" (AXHE-4473). The column here holds only that line plus
  // the compare-with checkbox underneath it; the controls themselves never stack.
  conditionRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  // Wraps rather than shrinks: the panel is capped at the viewport, so on a
  // narrow window there is not room for three controls side by side. Without
  // this the row still fits — a select is `overflow: hidden`, so its min-content
  // width is almost nothing and flex shrinks all three past legibility instead,
  // which reads as a layout that works and is really "does not equ…".
  conditionControls: {
    display: 'flex',
    flexWrap: 'wrap',
    // Not `center`: a control carrying a validation message is taller than its
    // neighbours, and centring would then float the others out of line with it.
    alignItems: 'flex-start',
    gap: 8,
  },
  // The field name is the longest of the three and the one worth the extra room
  // — an account's data keys are named by the account. `minWidth` is what makes
  // the row wrap: matching the basis stops flex-shrink before the text does.
  conditionField: {
    flex: '1.4 1 160px',
    minWidth: 160,
  },
  conditionOperator: {
    flex: '1 1 140px',
    minWidth: 140,
  },
  conditionValue: {
    flex: '1 1 140px',
    minWidth: 140,
  },
  // Matched to the input line rather than offset from it by arithmetic: 16px is
  // the standard-variant input's own top margin and 32px is its height, so a box
  // of those dimensions lands on the input whatever size the icon inside it is —
  // an `IconButton` already centres its child both ways. It is also what takes
  // the target from the 22px the icon alone gives to a full 32px.
  conditionRemove: {
    marginTop: 16,
    width: 32,
    height: 32,
  },
  conditionRemoveIcon: {
    fontSize: 16,
    color: theme.palette.common.gray500,
  },
  // Between adjacent conditions, never after the last — it describes a join, and
  // there is nothing after the final row to join it to (AXHE-3930).
  //
  // Layout only: `Chip size="small" variant="outlined"` already is the pill, so
  // the height, radius, border and hover/focus states come from the design system
  // rather than being restated here. The margin is what makes it read as a join
  // rather than as a control belonging to the row above — at the section's 8px
  // gap alone it sits directly under that row's compare-with checkbox and groups
  // with it.
  connector: {
    alignSelf: 'flex-start',
    margin: '8px 0',
  },
  // Holds the builder's place while the field catalog loads, so the section does
  // not read as "this level has no conditions" for the length of the request.
  conditionsPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 8,
  },
}));
